/**
 * API Health Check Service
 * Monitors API server status and provides health information
 */

import { appConfig, isDevelopment } from '../config/appConfig.js';

class HealthCheckService {
    constructor() {
        // In production, use the nginx health endpoint, in development use API direct
        this.apiBaseUrl = isDevelopment()
            ? 'http://localhost:4001'
            : '';
        this.healthEndpoint = '/api/health';
        this.checkInterval = 30000; // 30 seconds
        this.intervalId = null;
    }

    /**
     * Check API server health
     * @returns {Promise<Object>} Health status object
     */
    async checkHealth() {
        const startTime = performance.now();

        try {
            const response = await fetch(`${this.apiBaseUrl}${this.healthEndpoint}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
                signal: AbortSignal.timeout(5000) // 5 second timeout
            });

            const responseTime = performance.now() - startTime;

            if (!response.ok) {
                throw new Error(`API returned status ${response.status}`);
            }

            const data = await response.json();

            // Log successful health check with details
            console.log('%c✅ API Server Health Check Passed', 'color: #4CAF50; font-weight: bold; font-size: 14px');
            console.log('%c📊 API Server Status:', 'color: #2196F3; font-weight: bold', {
                status: data.status || 'healthy',
                version: data.version,
                environment: data.environment,
                responseTime: `${responseTime.toFixed(2)}ms`,
                timestamp: data.timestamp || new Date().toISOString(),
                endpoint: `${this.apiBaseUrl}${this.healthEndpoint}`
            });

            return {
                healthy: true,
                status: data.status || 'healthy',
                version: data.version,
                environment: data.environment,
                responseTime,
                timestamp: data.timestamp,
                data
            };
        } catch (error) {
            // Log error with details
            console.error('%c❌ API Server Health Check Failed', 'color: #f44336; font-weight: bold; font-size: 14px');
            console.error('%c🚨 Error Details:', 'color: #ff9800; font-weight: bold', {
                message: error.message,
                apiUrl: this.apiBaseUrl,
                endpoint: this.healthEndpoint,
                fullUrl: `${this.apiBaseUrl}${this.healthEndpoint}`,
                responseTime: `${(performance.now() - startTime).toFixed(2)}ms`,
                timestamp: new Date().toISOString()
            });

            // Additional helpful error messages
            if (error.name === 'AbortError') {
                console.error('%c⏱️ Request timed out after 5 seconds', 'color: #ff9800');
            } else if (error.message.includes('Failed to fetch')) {
                console.error('%c🔌 Cannot connect to API server', 'color: #ff9800');
                console.error('%c💡 Ensure the API server is running on ' + this.apiBaseUrl, 'color: #ff9800');
                console.error('%c📝 Start the API with: cd api && npm start', 'color: #ff9800; font-family: monospace');
            }

            return {
                healthy: false,
                error: error.message,
                responseTime: performance.now() - startTime,
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Perform initial health check with retry
     * @param {number} maxRetries - Maximum number of retries
     * @param {number} retryDelay - Delay between retries in milliseconds
     * @returns {Promise<Object>} Health status object
     */
    async initialHealthCheck(maxRetries = 3, retryDelay = 2000) {
        console.log('%c🔍 Checking API Server Health...', 'color: #2196F3; font-weight: bold; font-size: 16px');

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            const result = await this.checkHealth();

            if (result.healthy) {
                return result;
            }

            if (attempt < maxRetries) {
                console.warn(`%c🔄 Retrying health check (${attempt}/${maxRetries}) in ${retryDelay/1000} seconds...`,
                    'color: #ff9800');
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }

        // Final failure message
        console.error('%c❌ API Server is not responding after ' + maxRetries + ' attempts',
            'color: #f44336; font-weight: bold; font-size: 14px');
        console.error('%c⚠️ The application may not function properly without the API server',
            'color: #f44336');

        return {
            healthy: false,
            error: 'API server not responding after ' + maxRetries + ' attempts',
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Start periodic health checks
     * @param {number} interval - Interval in milliseconds (default: 30000)
     */
    startPeriodicCheck(interval = this.checkInterval) {
        if (this.intervalId) {
            this.stopPeriodicCheck();
        }

        console.log(`%c🔄 Starting periodic health checks every ${interval/1000} seconds`,
            'color: #2196F3');

        this.intervalId = setInterval(async () => {
            const result = await this.checkHealth();
            if (!result.healthy) {
                console.warn('%c⚠️ API server health check failed during periodic check',
                    'color: #ff9800');
            }
        }, interval);
    }

    /**
     * Stop periodic health checks
     */
    stopPeriodicCheck() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
            console.log('%c⏹️ Stopped periodic health checks', 'color: #2196F3');
        }
    }

    /**
     * Get a formatted health status message
     * @param {Object} healthResult - Result from health check
     * @returns {string} Formatted status message
     */
    getStatusMessage(healthResult) {
        if (healthResult.healthy) {
            return `API Server is healthy (v${healthResult.version}, ${healthResult.environment})`;
        } else {
            return `API Server is not responding: ${healthResult.error}`;
        }
    }
}

// Create singleton instance
const healthCheckService = new HealthCheckService();

export { healthCheckService };